import { AnimatePresence, motion } from 'motion/react';
import { Suspense, use, useRef } from 'react';

import { AtlasAiBackend } from './backends/AtlasAiBackend';
import classes from './Chat.module.css';
import { ChatInputForm } from './ChatInputForm';
import { ChatContext } from './contexts/ChatContext';
import { AssistantMessage, ChatMessage, ToolMessage, UserMessage } from './messages';
import type { ChatCompletionMessage } from './types';
import { ChatErrorBoundary } from '#components/chat/components/ChatErrorBoundary';
import { StreamingMessage } from '#components/chat/components/StreamingMessage';
import { LabeledLoader } from '#components/layout/LabeledLoader';
import { useMouseTracker } from '#hooks/useMouseTracker';

export interface ChatProps {
  backend?: React.ComponentType & { Hero: React.ComponentType };
}

export function Chat({ backend: BackendElement = AtlasAiBackend }: ChatProps) {
  const { messages, addMessage, streamingMessage } = use(ChatContext);

  const shouldDisplayHero = !messages.filter(({ message: { role } }) => role !== 'system').length;
  const historyRef = useRef<HTMLDivElement>(null);

  const roleMessageElement = {
    user: UserMessage,
    assistant: AssistantMessage,
    tool: ToolMessage,
    system: null,
    developer: null,
  } as const satisfies Record<ChatCompletionMessage['role'], React.ComponentType<Record<string, unknown>> | null>;
  const mouseTrackerRef = useMouseTracker();

  return (
    <div className={classes.chat} ref={mouseTrackerRef}>
      <BackendElement />
      <AnimatePresence>
        {shouldDisplayHero && (
          <div className={classes.hero} key="chat-hero">
            <BackendElement.Hero />
          </div>
        )}

        <div className={classes.history} ref={historyRef} key="chat-history">
          <div className={classes.messages}>
            <Suspense
              fallback={
                <div className={classes.loadingMessages}>
                  <LabeledLoader label="Loading messages..." />
                </div>
              }
            >
              {messages.map(({ id, message, timestamp, ...sourceMessageProps }) => {
                // TODO: zod parse/transform w/codec for handling timestamps etc.
                const Element = roleMessageElement[message.role];
                if (!Element) {
                  console.warn(`No message element for role: ${message.role}`);
                  return null;
                }
                return (
                  <ChatErrorBoundary key={id}>
                    <Element
                      id={id}
                      timestamp={new Date(timestamp)}
                      sourceMessageProps={sourceMessageProps}
                      {...message}
                    />
                  </ChatErrorBoundary>
                );
              })}
              {!!streamingMessage && <ChatMessage role="assistant" content={<StreamingMessage />} />}
            </Suspense>
          </div>
        </div>
      </AnimatePresence>

      <motion.div className={classes.input}>
        <ChatInputForm
          onSubmit={(message) => {
            addMessage({
              message: { role: 'user', content: message },
            });

            return new Promise((resolve) => {
              resolve(true);
            });
          }}
        />
      </motion.div>
    </div>
  );
}
